import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import talentApi from "../api/talent/talent-api-config";
import Paywall from "../components/paywall";
import { NextPlan } from "../models/next-plan.model";
import { AppState } from "../store/app-state";
import { sendProspect } from "../store/plan/plan-actions";
import "react-toastify/dist/ReactToastify.css";

export interface PaywallActionContextInterface {
  handleOpen: (val: boolean) => void;
  showPaywall: boolean;
}

export const PaywallActionContext = createContext<PaywallActionContextInterface>(
  {
    handleOpen: () => {},
    showPaywall: false,
  }
);

export const usePaywallActionContext = () => useContext(PaywallActionContext);

export const PaywallActionProvider: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const [showPaywall, setShowPaywall] = useState<boolean>(false);
  const [nextPlans, setNextPlans] = useState<NextPlan[]>([]);
  const [triggered, setTriggered] = useState<boolean>(false);
  const { user } = useSelector((state: AppState) => state.authState);

  useEffect(() => {
    talentApi.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response.status === 402) {
          setShowPaywall(true);
          if (`${error.response.data?.code}`.toUpperCase() === "PAYWALL") {
            try {
              setNextPlans(error.response.data.next_plans as NextPlan[]);
            } catch (error) {
              console.log(error);
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }, [setNextPlans]);

  const handleClose = useCallback(() => {
    setShowPaywall(false);
    setTriggered(false);
  }, [setShowPaywall]);

  const handleAccept = useCallback(async () => {
    const first_name = user?.name?.split(" ")[0];
    const last_name = user?.name?.split(" ")[1];
    const prospect = {
      email: user?.email ?? "",
      first_name: first_name ?? "",
      last_name: last_name ?? "",
      phone: user?.phone ?? "",
      product_id: nextPlans[0]?.hubspot_id ?? "",
      company_name: user?.corporate?.name ?? "",
      company_position: user?.company_position ?? "",
      total_employees: user?.corporate?.size ?? "",
      source: 'talent-match-plataform',
    };
    dispatch(sendProspect(prospect));
    setTriggered(true);
  }, [user, nextPlans, dispatch]);

  return (
    <PaywallActionContext.Provider
      value={{
        handleOpen: setShowPaywall,
        showPaywall: showPaywall,
      }}
    >
      {showPaywall && (
        <Paywall
          handleClose={handleClose}
          handleAccept={() => handleAccept()}
          plans={nextPlans}
          triggered={triggered}
        />
      )}
      <ToastContainer />
      {children}
    </PaywallActionContext.Provider>
  );
};
